// ─── Admin: Section 105 Plan Advisor Editor ───────────────────────
// Lives inside the IBOS Layers editor as the second tab.
// Edits plan types (specs, talking points, deliverables) + document templates + IRS limits.
import React, { useState, useEffect, useMemo } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { C, S } from '../../utils/styles';
import { useToast } from '../Toast';
import Icon from '../Icon';
import HelpSection from './HelpSection';
import {
  SEED_105_PLAN_TYPES,
  SEED_105_QUESTIONS,
  SEED_105_IRS_LIMITS,
  SEED_105_DOCUMENT_TEMPLATES,
  SEED_105_PACKAGES,
  SEED_105_COVERAGE_LAYERS,
} from '../../data/section105Defaults';

export default function Section105AdvisorEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();

  const [planTypes, setPlanTypes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [irsLimits, setIrsLimits] = useState({});
  const [documentTemplates, setDocumentTemplates] = useState({});
  const [packages, setPackages] = useState([]);
  const [coverageLayers, setCoverageLayers] = useState({});

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [activeSubview, setActiveSubview] = useState('packages'); // packages | plans | coverage | templates | limits
  const [editingDocKey, setEditingDocKey] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const s = config?.section105Config || {};
    setPlanTypes(JSON.parse(JSON.stringify(s.planTypes || SEED_105_PLAN_TYPES)));
    setQuestions(JSON.parse(JSON.stringify(s.questions || SEED_105_QUESTIONS)));
    setIrsLimits(JSON.parse(JSON.stringify(s.irsLimits || SEED_105_IRS_LIMITS)));
    setDocumentTemplates(JSON.parse(JSON.stringify(s.documentTemplates || SEED_105_DOCUMENT_TEMPLATES)));
    setPackages(JSON.parse(JSON.stringify(s.packages || SEED_105_PACKAGES)));
    setCoverageLayers(JSON.parse(JSON.stringify(s.coverageLayers || SEED_105_COVERAGE_LAYERS)));
    if (!selectedPlanId && (s.planTypes || SEED_105_PLAN_TYPES).length > 0) {
      setSelectedPlanId((s.planTypes || SEED_105_PLAN_TYPES)[0].id);
    }
    if (!selectedPackageId && (s.packages || SEED_105_PACKAGES).length > 0) {
      setSelectedPackageId((s.packages || SEED_105_PACKAGES)[0].id);
    }
  }, [config?.section105Config]); // eslint-disable-line

  const selectedPlan = useMemo(
    () => planTypes.find((p) => p.id === selectedPlanId),
    [planTypes, selectedPlanId]
  );
  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === selectedPackageId),
    [packages, selectedPackageId]
  );

  const updatePlan = (updater) => {
    setPlanTypes((prev) => prev.map((p) => (p.id === selectedPlanId ? updater(p) : p)));
    setHasChanges(true);
  };

  const updateSpec = (key, value) => {
    updatePlan((p) => ({ ...p, specs: { ...(p.specs || {}), [key]: value } }));
  };

  const updatePackage = (updater) => {
    setPackages((prev) => prev.map((p) => (p.id === selectedPackageId ? updater(p) : p)));
    setHasChanges(true);
  };

  const updateCoverage = (id, updater) => {
    setCoverageLayers((prev) => ({ ...prev, [id]: updater(prev[id] || {}) }));
    setHasChanges(true);
  };

  const handleResetAll = () => {
    if (!window.confirm('Reset all Section 105 Plan Advisor data to defaults? Plan types, packages, coverage layers, IRS limits, and document templates will be overwritten.')) return;
    setPlanTypes(JSON.parse(JSON.stringify(SEED_105_PLAN_TYPES)));
    setQuestions(JSON.parse(JSON.stringify(SEED_105_QUESTIONS)));
    setIrsLimits(JSON.parse(JSON.stringify(SEED_105_IRS_LIMITS)));
    setDocumentTemplates(JSON.parse(JSON.stringify(SEED_105_DOCUMENT_TEMPLATES)));
    setPackages(JSON.parse(JSON.stringify(SEED_105_PACKAGES)));
    setCoverageLayers(JSON.parse(JSON.stringify(SEED_105_COVERAGE_LAYERS)));
    setHasChanges(true);
    showToast('Reset to defaults. Click Save to persist.', 'info');
  };

  const save = async () => {
    try {
      setSaving(true);
      await updateConfig({
        section105Config: {
          planTypes,
          questions,
          irsLimits,
          documentTemplates,
          packages,
          coverageLayers,
        },
      });
      setHasChanges(false);
      showToast('Section 105 Plan Advisor saved successfully', 'success');
    } catch (err) {
      console.error('Failed to save Section 105 advisor config:', err);
      showToast('Failed to save Section 105 advisor config', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="s105-admin-editor" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ ...S.heading, margin: 0 }}>Section 105 Plan Advisor</h2>
          <p style={{ fontSize: 12, color: C.textMid, margin: '4px 0 0' }}>
            Manage plan types, IRS limits, and document templates used by the wizard.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleResetAll}
            style={{ ...S.buttonSecondary, fontSize: 12 }}
            data-testid="s105-admin-reset-btn"
          >
            <Icon name="refresh-outline" style={{ fontSize: 14, marginRight: 4 }} /> Reset to Defaults
          </button>
          <button
            onClick={save}
            disabled={!hasChanges || saving}
            style={{
              ...S.buttonPrimary,
              fontSize: 12,
              opacity: hasChanges && !saving ? 1 : 0.5,
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
            }}
            data-testid="s105-admin-save-btn"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Subnav */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: 4,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          alignSelf: 'flex-start',
        }}
      >
        {[
          { id: 'packages', label: 'Packages' },
          { id: 'plans', label: 'Plan Types' },
          { id: 'coverage', label: 'Coverage Layers' },
          { id: 'templates', label: 'Document Templates' },
          { id: 'limits', label: 'IRS Limits' },
        ].map((t) => {
          const active = activeSubview === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveSubview(t.id)}
              data-testid={`s105-admin-subtab-${t.id}`}
              style={{
                padding: '7px 14px',
                background: active ? C.card : 'transparent',
                color: active ? C.text : C.textMid,
                border: 'none',
                borderRadius: 7,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeSubview === 'packages' && (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 14 }}>
          {/* Package list */}
          <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textLight }}>
              Packages
            </div>
            <div>
              {packages.map((p) => {
                const active = p.id === selectedPackageId;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPackageId(p.id)}
                    data-testid={`s105-admin-package-${p.id}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 4,
                      padding: '10px 14px',
                      border: 'none',
                      borderBottom: `1px solid ${C.borderLight}`,
                      background: active ? C.accentBg : 'transparent',
                      color: active ? C.accent : C.text,
                      fontSize: 12,
                      fontWeight: active ? 700 : 600,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{p.name}</span>
                      {p.flagged && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fff3e0', color: C.orange, fontWeight: 800 }}>⚠</span>}
                    </div>
                    <div style={{ fontSize: 10, color: C.textLight, fontWeight: 500 }}>
                      ${p.estMonthlyEmployerCost}/ee/mo · {p.section105Type} {p.coverageLayer ? `+ ${p.coverageLayer}` : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Package editor */}
          {selectedPackage && (
            <div style={{ ...S.card }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <h3 style={{ ...S.heading, margin: 0 }}>{selectedPackage.name}</h3>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: C.accentBg, color: C.accent }}>
                  {selectedPackage.section105Type}{selectedPackage.coverageLayer ? ` + ${selectedPackage.coverageLayer}` : ''}
                </span>
                {selectedPackage.flagged && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: '#fff3e0', color: C.orange }}>
                    ⚠ Legal review required
                  </span>
                )}
              </div>

              <Sub title="Headlines">
                <Field label="Internal headline">
                  <input type="text" value={selectedPackage.headlineInternal || ''} onChange={(e) => updatePackage((p) => ({ ...p, headlineInternal: e.target.value }))} style={S.input} />
                </Field>
                <Field label="Employer-facing headline">
                  <input type="text" value={selectedPackage.headlineEmployer || ''} onChange={(e) => updatePackage((p) => ({ ...p, headlineEmployer: e.target.value }))} style={S.input} />
                </Field>
              </Sub>

              <Sub title="Why this package">
                <Field label="Why this package (Internal)">
                  <textarea value={selectedPackage.whyThisPackage || ''} onChange={(e) => updatePackage((p) => ({ ...p, whyThisPackage: e.target.value }))} style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
                <Field label="Why this package (Employer)">
                  <textarea value={selectedPackage.whyThisPackageEmployer || ''} onChange={(e) => updatePackage((p) => ({ ...p, whyThisPackageEmployer: e.target.value }))} style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
              </Sub>

              <Sub title="Cost & Setup">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                  <Field label="Est. monthly employer cost ($/ee)">
                    <input type="number" value={selectedPackage.estMonthlyEmployerCost || 0} onChange={(e) => updatePackage((p) => ({ ...p, estMonthlyEmployerCost: parseFloat(e.target.value) || 0 }))} style={S.input} />
                  </Field>
                  <Field label="Cost range LOW ($)">
                    <input type="number" value={selectedPackage.employerCostRange?.[0] ?? 0} onChange={(e) => updatePackage((p) => ({ ...p, employerCostRange: [parseFloat(e.target.value) || 0, p.employerCostRange?.[1] ?? 0] }))} style={S.input} />
                  </Field>
                  <Field label="Cost range HIGH ($)">
                    <input type="number" value={selectedPackage.employerCostRange?.[1] ?? 0} onChange={(e) => updatePackage((p) => ({ ...p, employerCostRange: [p.employerCostRange?.[0] ?? 0, parseFloat(e.target.value) || 0] }))} style={S.input} />
                  </Field>
                  <Field label="Setup time">
                    <input type="text" value={selectedPackage.setupWeeks || ''} onChange={(e) => updatePackage((p) => ({ ...p, setupWeeks: e.target.value }))} style={S.input} />
                  </Field>
                  <Field label="Legal review">
                    <select value={selectedPackage.legalReview || 'Standard'} onChange={(e) => updatePackage((p) => ({ ...p, legalReview: e.target.value }))} style={S.input}>
                      <option value="Standard">Standard</option>
                      <option value="Required">Required</option>
                    </select>
                  </Field>
                </div>
              </Sub>

              <Sub title="Pros (one per line)">
                <Field label="">
                  <textarea value={(selectedPackage.pros || []).join('\n')} onChange={(e) => updatePackage((p) => ({ ...p, pros: e.target.value.split('\n').filter(Boolean) }))} style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
              </Sub>

              <Sub title="Cons (one per line)">
                <Field label="">
                  <textarea value={(selectedPackage.cons || []).join('\n')} onChange={(e) => updatePackage((p) => ({ ...p, cons: e.target.value.split('\n').filter(Boolean) }))} style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
              </Sub>

              <Sub title="Best for">
                <Field label="Best for (Internal)">
                  <input type="text" value={selectedPackage.bestFor || ''} onChange={(e) => updatePackage((p) => ({ ...p, bestFor: e.target.value }))} style={S.input} />
                </Field>
                <Field label="Best for (Employer)">
                  <input type="text" value={selectedPackage.bestForEmployer || ''} onChange={(e) => updatePackage((p) => ({ ...p, bestForEmployer: e.target.value }))} style={S.input} />
                </Field>
              </Sub>

              <Sub title="Compliance Notes (one per line)">
                <Field label="">
                  <textarea value={(selectedPackage.complianceNotes || []).join('\n')} onChange={(e) => updatePackage((p) => ({ ...p, complianceNotes: e.target.value.split('\n').filter(Boolean) }))} style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
              </Sub>
            </div>
          )}
        </div>
      )}

      {activeSubview === 'coverage' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Object.values(coverageLayers).map((cov) => (
            <div key={cov.id} style={{ ...S.card }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <Icon name={cov.icon || 'shield-checkmark-outline'} style={{ fontSize: 18, color: C.accent }} />
                <h3 style={{ ...S.heading, margin: 0 }}>{cov.name}</h3>
                <span style={{ fontSize: 10, color: C.textLight }}>{cov.fullName}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
                <Field label="Monthly cost estimate ($/ee)">
                  <input
                    type="number"
                    value={cov.monthlyCostEstimate || 0}
                    onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, monthlyCostEstimate: parseFloat(e.target.value) || 0 }))}
                    data-testid={`s105-admin-cov-${cov.id}-cost`}
                    style={S.input}
                  />
                </Field>
                <Field label="Range LOW ($)">
                  <input type="number" value={cov.monthlyCostRange?.[0] ?? 0} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, monthlyCostRange: [parseFloat(e.target.value) || 0, c.monthlyCostRange?.[1] ?? 0] }))} style={S.input} />
                </Field>
                <Field label="Range HIGH ($)">
                  <input type="number" value={cov.monthlyCostRange?.[1] ?? 0} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, monthlyCostRange: [c.monthlyCostRange?.[0] ?? 0, parseFloat(e.target.value) || 0] }))} style={S.input} />
                </Field>
                <Field label="Provider (optional)">
                  <input type="text" value={cov.provider || ''} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, provider: e.target.value }))} style={S.input} />
                </Field>
                <Field label="Status">
                  <select value={cov.status || 'available'} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, status: e.target.value }))} style={S.input}>
                    <option value="active">Active</option>
                    <option value="available">Available</option>
                    <option value="future">Future</option>
                  </select>
                </Field>
              </div>
              <Field label="Employer summary">
                <textarea value={cov.employerSummary || ''} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, employerSummary: e.target.value }))} style={{ ...S.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
              <Field label="Employee summary">
                <textarea value={cov.employeeSummary || ''} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, employeeSummary: e.target.value }))} style={{ ...S.input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
              <Field label="Benefits (one per line)">
                <textarea value={(cov.benefits || []).join('\n')} onChange={(e) => updateCoverage(cov.id, (c) => ({ ...c, benefits: e.target.value.split('\n').filter(Boolean) }))} style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
              {cov.id === 'health_share' && (
                <div style={{ marginTop: 10, padding: 12, background: '#fff3e0', border: `1.5px solid ${C.orange}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    Required disclaimers (compliance-locked — read-only)
                  </div>
                  <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
                    {(cov.requiredDisclaimers || []).map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeSubview === 'plans' && selectedPlan && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
          {/* Plan list */}
          <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textLight }}>
              Plan Types
            </div>
            <div>
              {planTypes.map((p) => {
                const active = p.id === selectedPlanId;
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlanId(p.id)}
                    data-testid={`s105-admin-plan-${p.id}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      border: 'none',
                      borderBottom: `1px solid ${C.borderLight}`,
                      background: active ? C.accentBg : 'transparent',
                      color: active ? C.accent : C.text,
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 26, height: 26, borderRadius: 6, background: active ? C.accent : C.bg, color: active ? '#fff' : C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name="medical-outline" style={{ fontSize: 13 }} />
                    </span>
                    <span style={{ flex: 1 }}>
                      <div>{p.type}</div>
                      <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>{p.tagline}</div>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plan editor */}
          <div style={{ ...S.card }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <h3 style={{ ...S.heading, margin: 0 }}>{selectedPlan.type}</h3>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: C.accentBg, color: C.accent }}>
                {selectedPlan.fullName}
              </span>
            </div>

            <Sub title="Basic Info">
              <Field label="Plan Type Code">
                <input
                  type="text"
                  value={selectedPlan.type}
                  onChange={(e) => updatePlan((p) => ({ ...p, type: e.target.value }))}
                  style={S.input}
                />
              </Field>
              <Field label="Full Name">
                <input
                  type="text"
                  value={selectedPlan.fullName}
                  onChange={(e) => updatePlan((p) => ({ ...p, fullName: e.target.value }))}
                  style={S.input}
                />
              </Field>
              <Field label="Tagline">
                <input
                  type="text"
                  value={selectedPlan.tagline}
                  onChange={(e) => updatePlan((p) => ({ ...p, tagline: e.target.value }))}
                  style={S.input}
                />
              </Field>
              <Field label="Eligibility Summary">
                <input
                  type="text"
                  value={selectedPlan.eligibility}
                  onChange={(e) => updatePlan((p) => ({ ...p, eligibility: e.target.value }))}
                  style={S.input}
                />
              </Field>
              <Field label="Description (Internal)">
                <textarea
                  value={selectedPlan.description}
                  onChange={(e) => updatePlan((p) => ({ ...p, description: e.target.value }))}
                  style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Field>
              <Field label="Description (Employer-facing)">
                <textarea
                  value={selectedPlan.descriptionEmployer || ''}
                  onChange={(e) => updatePlan((p) => ({ ...p, descriptionEmployer: e.target.value }))}
                  style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Field>
            </Sub>

            <Sub title="Specs">
              {Object.entries(selectedPlan.specs || {}).map(([k, v]) => (
                <Field key={k} label={prettyLabel(k)}>
                  <input
                    type="text"
                    value={v || ''}
                    onChange={(e) => updateSpec(k, e.target.value)}
                    style={S.input}
                  />
                </Field>
              ))}
            </Sub>

            <Sub title="Key Rules (one per line)">
              <Field label="">
                <textarea
                  value={(selectedPlan.keyRules || []).join('\n')}
                  onChange={(e) =>
                    updatePlan((p) => ({
                      ...p,
                      keyRules: e.target.value.split('\n').filter(Boolean),
                    }))
                  }
                  style={{ ...S.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Field>
            </Sub>

            <Sub title="Talking Points — Internal (one per line)">
              <Field label="">
                <textarea
                  value={(selectedPlan.talkingPointsInternal || []).join('\n')}
                  onChange={(e) =>
                    updatePlan((p) => ({
                      ...p,
                      talkingPointsInternal: e.target.value.split('\n').filter(Boolean),
                    }))
                  }
                  style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Field>
            </Sub>

            <Sub title="Talking Points — Employer (one per line)">
              <Field label="">
                <textarea
                  value={(selectedPlan.talkingPointsEmployer || []).join('\n')}
                  onChange={(e) =>
                    updatePlan((p) => ({
                      ...p,
                      talkingPointsEmployer: e.target.value.split('\n').filter(Boolean),
                    }))
                  }
                  style={{ ...S.input, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </Field>
            </Sub>

            <Sub title="Deliverables">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(selectedPlan.deliverables || []).map((d, i) => (
                  <div
                    key={d.id || i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '160px 1fr 1fr',
                      gap: 6,
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="text"
                      value={d.id}
                      onChange={(e) =>
                        updatePlan((p) => ({
                          ...p,
                          deliverables: p.deliverables.map((x, idx) => (idx === i ? { ...x, id: e.target.value } : x)),
                        }))
                      }
                      style={{ ...S.input, fontSize: 11 }}
                      placeholder="Doc key (e.g. merp_plan_doc)"
                    />
                    <input
                      type="text"
                      value={d.title}
                      onChange={(e) =>
                        updatePlan((p) => ({
                          ...p,
                          deliverables: p.deliverables.map((x, idx) => (idx === i ? { ...x, title: e.target.value } : x)),
                        }))
                      }
                      style={{ ...S.input, fontSize: 11 }}
                      placeholder="Title"
                    />
                    <input
                      type="text"
                      value={d.description}
                      onChange={(e) =>
                        updatePlan((p) => ({
                          ...p,
                          deliverables: p.deliverables.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)),
                        }))
                      }
                      style={{ ...S.input, fontSize: 11 }}
                      placeholder="Description"
                    />
                  </div>
                ))}
              </div>
            </Sub>
          </div>
        </div>
      )}

      {activeSubview === 'templates' && (
        <div style={{ ...S.card }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <h3 style={{ ...S.heading, margin: 0 }}>Document Templates</h3>
            <span style={{ fontSize: 10, color: C.textLight, fontWeight: 600 }}>
              {Object.keys(documentTemplates).length} templates · markdown with {'{merge_field}'} placeholders
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 12 }}>
            {Object.keys(documentTemplates).map((key) => {
              const isEditing = editingDocKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setEditingDocKey(key)}
                  data-testid={`s105-admin-tpl-${key}`}
                  style={{
                    padding: '8px 12px',
                    background: isEditing ? C.accentBg : C.card,
                    border: `1px solid ${isEditing ? C.accent : C.border}`,
                    color: isEditing ? C.accent : C.text,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="document-outline" style={{ fontSize: 13 }} />
                    {key}
                  </div>
                </button>
              );
            })}
          </div>
          {editingDocKey && (
            <div style={{ marginTop: 8 }}>
              <Field label={`Template: ${editingDocKey}`}>
                <textarea
                  value={documentTemplates[editingDocKey] || ''}
                  onChange={(e) => {
                    setDocumentTemplates((prev) => ({ ...prev, [editingDocKey]: e.target.value }));
                    setHasChanges(true);
                  }}
                  data-testid={`s105-admin-tpl-textarea-${editingDocKey}`}
                  style={{
                    ...S.input,
                    minHeight: 320,
                    resize: 'vertical',
                    fontFamily: "'Source Code Pro', monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                  }}
                />
              </Field>
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 6 }}>
                Available merge fields: <code>{'{company_name}'}</code> <code>{'{effective_date}'}</code> <code>{'{administrator_name}'}</code> <code>{'{contribution_amount}'}</code> <code>{'{eligible_classes}'}</code> <code>{'{eligibility_waiting_period}'}</code> <code>{'{plan_year_start}'}</code> <code>{'{class_contributions}'}</code> <code>{'{bridged_count}'}</code> <code>{'{bridge_savings}'}</code>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSubview === 'limits' && (
        <div style={{ ...S.card }}>
          <h3 style={{ ...S.heading, marginTop: 0 }}>IRS Limits & Constants</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            {Object.entries(irsLimits).map(([key, value]) => (
              <Field key={key} label={prettyLabel(key)}>
                <input
                  type="number"
                  step="any"
                  value={value}
                  onChange={(e) => {
                    setIrsLimits((prev) => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }));
                    setHasChanges(true);
                  }}
                  data-testid={`s105-admin-limit-${key}`}
                  style={S.input}
                />
              </Field>
            ))}
          </div>
        </div>
      )}

      <HelpSection title="Section 105 Plan Advisor Help" storageKey="s105-advisor-help">
        <p><strong>What is this?</strong> The Section 105 Plan Advisor is a 6-question wizard that maps employer profiles to one of 5 HRA plan types: QSEHRA, Excepted Benefit HRA, ICHRA, Section 105 MERP, or Group Coverage HRA.</p>
        <p><strong>Plan Types tab:</strong> Edit the recommended plan content shown to internal users and employers. Internal-only fields (description, talking points) are hidden in employer-facing screens.</p>
        <p><strong>Document Templates tab:</strong> Each deliverable can have an associated markdown template with <code>{'{merge_field}'}</code> placeholders. Templates are rendered client-side when a user clicks &ldquo;View sample document&rdquo;.</p>
        <p><strong>IRS Limits tab:</strong> Annual contribution caps and ACA thresholds. Update once per year as the IRS releases new figures.</p>
        <p><strong>Wizard flow:</strong> Internal users access the wizard from the Client Drawer &rarr; IBOS Configuration &rarr; Section 105 Plan Advisor. Employers see the wizard on their dashboard and request a consultation when ready.</p>
      </HelpSection>
    </div>
  );
}

function Sub({ title, children }) {
  return (
    <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${C.borderLight}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label style={{ ...S.label, display: 'block', marginBottom: 4 }}>{label}</label>}
      {children}
    </div>
  );
}

function prettyLabel(key) {
  const map = {
    qsehra_individual_cap: 'QSEHRA Individual Cap ($/yr)',
    qsehra_family_cap: 'QSEHRA Family Cap ($/yr)',
    excepted_benefit_cap: 'Excepted Benefit HRA Cap ($/yr)',
    aca_affordability_pct: 'ACA Affordability % (e.g. 9.12)',
    aca_penalty_a: 'ACA §4980H(a) Penalty ($/yr)',
    year: 'Plan Year',
    contributionCaps: 'Contribution caps',
    complexity: 'Complexity',
    setupTime: 'Setup time',
    erisa: 'ERISA',
    acaImpact: 'ACA impact',
    nonDiscrimination: 'Non-discrimination',
    requiresMEC: 'Coverage requirement',
  };
  return map[key] || key.replace(/([A-Z_])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}
